// src/App.js

import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios"; // <-- Import d'axios pour l'interceptor
import StageView from "./components/StageView";
import Login from "./components/Login";
import Home from "./components/Home"; // Vitrine
import Sessions from "./components/Sessions"; // Liste des sessions
import Profile from "./components/Profile";
import Terms from "./components/Terms";       // Page des mentions légales
import Privacy from "./components/Privacy";   // Page de la politique de confidentialité

function App() {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [isSessionChecked, setIsSessionChecked] = useState(false);

  // Vérification de la session utilisateur au montage uniquement
  useEffect(() => {
    verifyUserSession();
  }, []);

  // 1) Interceptor pour gérer token expiré / invalide
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Si le backend renvoie 401 ou 403, on redirige vers login
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          localStorage.removeItem("user");
          setUser(null);
          navigate("/login");
        }
        return Promise.reject(error);
      }
    );

    return () => {
      // On enlève l'interceptor lorsqu'on démonte le composant
      axios.interceptors.response.eject(interceptor);
    };
  }, [navigate]);

  // 3) Gestion du callback Google
  useEffect(() => {
    const handleGoogleCallback = async () => {
      if (location.pathname === "/auth/google/callback") {
        const params = new URLSearchParams(location.search);
        const token = params.get("token");

        if (token) {
          try {
            const response = await fetch("/api/auth/google/callback", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ token }),
            });

            if (response.ok) {
              const data = await response.json();
              if (data?.user) {
                localStorage.setItem("user", JSON.stringify(data.user));
                setUser(data.user);
                navigate("/");
              }
            }
          } catch (error) {
            console.error("Google callback error:", error);
            navigate("/login");
          }
        }
      }
    };

    handleGoogleCallback();
  }, [location, navigate]);

  // 4) Déconnexion
  const handleLogout = () => {
    localStorage.removeItem("user");
    setUser(null);
    navigate("/login");
  };

  const verifyUserSession = async () => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) {
      setIsSessionChecked(true);
      return;
    }

    try {
      const parsedUser = JSON.parse(storedUser);
      
      // Vérifier que le token est valide en appelant l'API
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/users/${parsedUser.id}`,
        {
          headers: { Authorization: `Bearer ${parsedUser.access_token}` },
        }
      );

      if (response.status === 200) {
        // Session valide, set user une seule fois
        setUser(parsedUser);
      } else {
        localStorage.removeItem("user");
        setUser(null);
      }
    } catch (error) {
      console.error("Erreur lors de la vérification de la session :", error);

      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("user");
        setUser(null);
      }
    } finally {
      setIsSessionChecked(true);
    }
  };

  return (
    <div className="App" style={{ margin: 0, padding: 0 }}>
      {/* Afficher un loader pendant la vérification */}
      {!isSessionChecked ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <div>Chargement de la session...</div>
        </div>
      ) : (
        <>
          {/* En-tête utilisateur */}
          <div
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              padding: "10px",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            {!location.pathname.startsWith("/sessions/") && (
              user ? (
                <img
                  src={user.profile_pic?.replace("=s96-c", "=s200-c")}
                  alt="Profile"
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    cursor: "pointer",
                  }}
                  onClick={() => navigate("/profile")}
                />
              ) : (
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    backgroundColor: "lightgray",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#fff",
                    fontSize: "18px",
                    fontWeight: "bold",
                  }}
                  onClick={() => navigate("/login")}
                >
                  👤
                </div>
              )
            )}

          </div>

          {/* Définition des routes */}
          <Routes>
            {/* Page d'accueil */}
            <Route path="/" element={<Home />} />

            {/* Page de login */}
            <Route
              path="/login"
              element={user ? <Navigate to="/sessions" /> : <Login setUser={setUser} />}
            />

            {/* Liste des sessions */}
            <Route
              path="/sessions"
              element={user ? <Sessions user={user} /> : <Navigate to="/login" />}
            />

            {/* Page d'édition d'une session */}
            <Route
              path="/sessions/:sessionId"
              element={user ? <StageView user={user} /> : <Navigate to="/login" />}
            />

            {/* Profil */}
            <Route
              path="/profile"
              element={
                isSessionChecked && user ? (
                  <Profile user={user} setUser={setUser} />
                ) : isSessionChecked ? (
                  <Navigate to="/login" />
                ) : (
                  <div>Chargement...</div> // Un petit message d'attente
                )
              }
            />

            {/* Pages statiques publiques */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />

            {/* Redirection par défaut */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </>
      )}
    </div>
  );
}

export default App;
